import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { Link } from 'react-router-dom';
import { trackRegisterStarted, trackRegisterCompleted } from '../utils/analytics';
import { trackGoogleAdsSignupConversion } from './GoogleAdsInit';
import { AuthCard, AuthCardTitle, AuthFormError, AuthTextButton } from './AuthCard';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

interface SignupProps {
  onSwitchToLogin: () => void;
}

export const Signup: React.FC<SignupProps> = ({ onSwitchToLogin }) => {
  const { signup } = useAuth();
  const { t, locale } = useLocale();
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [invitationCode, setInvitationCode] = useState<string>(() =>
    t('landing_early_access_invitation_code')
  );
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [acceptedTerms, setAcceptedTerms] = useState<boolean>(false);
  const registerStartedFired = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const invitationCodeParam = params.get('invitation_code');
    if (invitationCodeParam) {
      setInvitationCode(invitationCodeParam);
    }
    const emailParam = params.get('email');
    if (emailParam) {
      setEmail(emailParam);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError(t('passwords_not_match'));
      return;
    }

    if (password.length < 8) {
      setError(t('password_too_short'));
      return;
    }

    if (!acceptedTerms) {
      setError(t('must_accept_terms'));
      return;
    }

    setIsLoading(true);

    try {
      await signup({ email, password, invitationCode: invitationCode || undefined });
      trackRegisterCompleted(invitationCode || undefined, locale);
      trackGoogleAdsSignupConversion();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('signup_failed'));
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pt-8">
      <AuthCard>
        <AuthCardTitle>{t('signup_title')}</AuthCardTitle>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <Label htmlFor="invitation-code" className="mb-2 block">
              {t('invitation_code')} ({t('optional') || 'Optional'})
            </Label>
            <Input
              id="invitation-code"
              type="text"
              value={invitationCode}
              onChange={(e) => setInvitationCode(e.target.value)}
              disabled={isLoading}
            />
          </div>
          <div className="mb-4">
            <Label htmlFor="signup-email" className="mb-2 block">
              {t('email')}
            </Label>
            <Input
              id="signup-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (!registerStartedFired.current) {
                  registerStartedFired.current = true;
                  trackRegisterStarted(invitationCode || undefined, locale);
                }
              }}
              required
              disabled={isLoading}
            />
          </div>
          <div className="mb-4">
            <Label htmlFor="signup-password" className="mb-2 block">
              {t('password')}
            </Label>
            <Input
              id="signup-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              minLength={8}
            />
            <div className="mt-1 text-xs text-muted-foreground">
              {t('must_be_8_chars')}
            </div>
          </div>
          <div className="mb-6">
            <Label htmlFor="confirm-password" className="mb-2 block">
              {t('confirm_password')}
            </Label>
            <Input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>
          {error && <AuthFormError>{error}</AuthFormError>}
          <div className="mb-4">
            <label className="flex cursor-pointer items-start gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                disabled={isLoading}
                required
                className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer disabled:cursor-not-allowed"
              />
              <span>
                {t('signup_accepts_terms')}{' '}
                <Link
                  to="/terms"
                  className="underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t('terms_of_service_link')}
                </Link>
              </span>
            </label>
          </div>
          <Button
            type="submit"
            variant="inverse"
            className="mb-4 w-full"
            disabled={isLoading || !acceptedTerms}
          >
            {isLoading ? t('signing_up') : t('signup')}
          </Button>
          <div className="text-center text-sm text-muted-foreground">
            {t('already_have_account')}{' '}
            <AuthTextButton onClick={onSwitchToLogin}>
              {t('login')}
            </AuthTextButton>
          </div>
        </form>
      </AuthCard>
    </div>
  );
};
